import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, forwardRef } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { ImageModule } from '@df/module-image/image.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketService } from '@df/session/api/basket/basket.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { GtmImpressionTrackDirective } from 'projects/app-default/src/app/module/gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from 'projects/app-default/src/app/module/gtm/gtm-track-product-click.directive';
import { TooltipComponent } from 'projects/app-default/src/app/ui/common/tooltip/tooltip.component';
import { ScrollableComponent } from 'projects/app-default/src/app/ui/scrollable/scrollable.component';
import { NBuilderConfig } from '../../common/builder.config';
import { BuilderAddToBasketComponent } from '../../common/products-setup/add-to-basket/builder-add-to-basket.component';
import { BuilderProductsSetupComponentDef } from '../../common/products-setup/builder-products-setup.component.def';
import { NBuilderProductsSetup } from '../../common/products-setup/builder-products-setup.config';
import { BuilderGalleryComponent, IFocusData, IOptionImageData } from '../../common/products-setup/gallery/builder-gallery.component';
import { BuilderListingToolsComponent } from '../../common/products-setup/listing-tools/builder-listing-tools.component';
import { BuilderProductComponent } from '../../common/products-setup/product/builder-product.component';
import { KitBuilderService } from '../kit-builder.service';
import { NKitBuilder } from '../page-kit-builder-view.component';

@Component({
    selector: 'kit-products-setup',
    templateUrl: '../../common/products-setup/builder-products-setup.html',
    styleUrls: ['../../common/products-setup/builder-products-setup.scss'],
    standalone: true,
    imports: [
        forwardRef(() => BuilderAddToBasketComponent),
        BuilderGalleryComponent,
        BuilderListingToolsComponent,
        BuilderProductComponent,
        DfUiModule,
        DfDomModule,
        Ga4TrackClickDirective,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        ImageModule,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        PriceComponent,
        ScrollableComponent,
        SizeClassDirective,
        TooltipComponent,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KitProductsSetupComponent extends BuilderProductsSetupComponentDef {
    @Input({ required: true })
    content!: NKitBuilder.IPageData;
    @Input({ required: true })
    storageData!: NKitBuilder.IStorageData;
    override hideImageOnToggle: boolean = true;
    protected ponyOptionElements: NBuilderProductsSetup.IOptionElementData[] = [];
    protected riderOptionElements: NBuilderProductsSetup.IOptionElementData[] = [];
    protected override toggleOptions: NBuilderProductsSetup.IToggleOption[] = [
        { label: 'Toy', value: NBuilderProductsSetup.EToggleOptions.horse },
        { label: 'Rider', value: NBuilderProductsSetup.EToggleOptions.rider }
    ];
    protected useGA4 = false;
    constructor(
        app: App,
        changeDetectorRef: ChangeDetectorRef,
        cmsModalService: ICmsModalService,
        basketService: BasketService,
        timeoutService: TimeoutService,
        requestService: RequestService,
        private kitBuilderService: KitBuilderService
    ) {
        super(app, changeDetectorRef, cmsModalService, basketService, timeoutService, requestService);
    }
    protected reset(): void {
        this.kitBuilderService.goToStep(this.storageData, NKitBuilder.ESteps.pony);
    }
    protected undo(): void {
        if (this.storageData.selection![this.storageData.currentSectionOptionId || '']) {
            this.unselectCurrentOption();
        } else if (!Object.values(this.storageData.selection!).find(v => v)) {
            this.kitBuilderService.goToStep(this.storageData, NKitBuilder.ESteps.pony);
        } else {
            const prevSection =
                this.storageData.history!.prevSelectedSection ||
                Object.keys(this.storageData.selection!)
                    ?.map(Number)
                    ?.find(key => this.storageData.selection![key]);
            if (prevSection) {
                this.changeCurrentOption(prevSection);
                this.storageData.history!.prevSelectedSection = undefined;
            }
        }
        this.changeDetectorRef.detectChanges();
    }
    // filter products by section option and remove options without products
    protected initOptionElements(): void {
        if (this.content.leftSectionPony?.length) {
            this.ponyOptionElements = this.prepareListings(
                this.content.leftSectionPony,
                (data: NBuilderConfig.IStorageData, sectionOptionId) =>
                    !!this.category
                        ? this.kitBuilderService
                              .getProducts(data as NKitBuilder.IStorageData, this.content.useToyAttr, sectionOptionId || 0)
                              ?.sort((a, b) => this.sortBycategory(a, b))
                        : this.kitBuilderService.getProducts(
                              data as NKitBuilder.IStorageData,
                              this.content.useToyAttr,
                              sectionOptionId || 0
                          )
            );
        }
        // prep rider options
        if (this.content.leftSectionRider?.length) {
            this.riderOptionElements = this.prepareListings(
                this.content.leftSectionRider,
                (data: NBuilderConfig.IStorageData, sectionOptionId) =>
                    !!this.category
                        ? this.kitBuilderService
                              .getProducts(data as NKitBuilder.IStorageData, this.content.useToyAttr, sectionOptionId || 0)
                              ?.sort((a, b) => this.sortBycategory(a, b))
                        : this.kitBuilderService.getProducts(
                              data as NKitBuilder.IStorageData,
                              this.content.useToyAttr,
                              sectionOptionId || 0
                          )
            );
        }
        if (!this.storageData.optionToggleState) {
            this.changeToggleState();
        } else if (this.storageData.optionToggleState === NBuilderProductsSetup.EToggleOptions.rider) {
            this.preselectRiderOptions();
        }
    }
    protected override updateBasketRequest(): void {
        this.basketRequest =
            Object.values(this.storageData.selection!)?.map(option => ({
                id: option.matchedProductId,
                qty: 1
            })) || [];
        const ponyProductId = this.content.firstStepOptions.find(o => o.name === this.storageData.pony?.optionName)?.productId;
        if (ponyProductId) {
            this.basketRequest.push({
                id: ponyProductId,
                qty: 1
            });
        }
        this.totalPrice = this.basketRequest.length ? this.basketService.calcTotalPrices(this.basketRequest)?.price : 0;
        this.scrollAfterBasketRequestUpdate();
    }
    getMainImage(): { image: string; altImage?: string } {
        return this.findImage(this.content.firstStepOptions, this.storageData.pony?.optionName || '');
    }
    protected selectAdditionalOption(name: string) {
        if (this.storageData.pony && this.storageData.pony.optionName !== name) {
            this.storageData.pony.optionName = name;
            this.changeDetectorRef.detectChanges();
            this.updateStorage();
        }
    }
    protected override isSelectedAdditionalOption(name: string) {
        return this.storageData.pony?.optionName === name;
    }

    get horseImageData(): IOptionImageData {
        if (!this.storageData.pony) {
            return { image: '', shift: 0, order: [] };
        }
        return {
            image: this.getMainImage().image,
            shift: (this.isRiderSelected() && this.content.ponyShiftWithRider) || 0,
            order: this.ponyOptionElements.map(section => ({
                id: section.sectionOption,
                useAdditionalLabel: false,
                hideForAttrOption: section.hideForAttrOption
            })),
            altImage: this.getMainImage().altImage
        };
    }

    get riderShift(): number {
        return this.content.riderShift || 0;
    }
    get riderImageData(): IOptionImageData {
        if (this.riderOptionElements.length) {
            return {
                image: undefined,
                shift: this.riderShift,
                order: this.riderOptionElements.map(section => ({
                    id: section.sectionOption,
                    useAdditionalLabel: false,
                    hideForAttrOption: section.hideForAttrOption
                })),
                withoutMainImage: true
            };
        }
        return { image: '', shift: 0, order: [], withoutMainImage: true };
    }
    get canShowToogle(): boolean {
        return this.riderOptionElements.length > 0;
    }
    get leftSectionOption(): NBuilderProductsSetup.IOptionElementData[] {
        return this.toggleState === NBuilderProductsSetup.EToggleOptions.rider ? this.riderOptionElements : this.ponyOptionElements;
    }
    override get toggleState(): NBuilderProductsSetup.EToggleOptions | undefined {
        return this.storageData.optionToggleState || NBuilderProductsSetup.EToggleOptions.horse;
    }
    get currentOption() {
        return this.toggleState === NBuilderProductsSetup.EToggleOptions.rider
            ? this.riderOptionElements.find(option => option.sectionOption === this.storageData.currentSectionOptionId)
            : this.ponyOptionElements.find(option => option.sectionOption === this.storageData.currentSectionOptionId);
    }

    get displayAltImage(): boolean {
        return !!this.ponyOptionElements.find(
            option => option.displayAdditionalImage && this.isSectionOptionSelected(option.sectionOption)
        );
    }

    get baseGalleryPosition(): IFocusData {
        return { posLeft: this.content.ponyShift || 0, posTop: 0, scale: 1 };
    }

    get additionalHorseStep(): NBuilderProductsSetup.IAdditionalHorseStep | undefined {
        return {
            id: NBuilderProductsSetup.ADDITIONAL_HORSE_SETP_ID,
            title: 'Pony',
            image: this.content.additionalHorseStepImage || '',
            options: this.content.firstStepOptions,
            squareVariant: true
        };
    }

    get isAdditionalStepState(): boolean {
        return this.toggleState === NBuilderProductsSetup.EToggleOptions.horse;
    }
    override get displayNewLabelForToggle(): boolean {
        return (
            this.content.displayNewLabelForToggle &&
            !this.storageData.riderViewVisited &&
            this.toggleState !== NBuilderProductsSetup.EToggleOptions.rider
        );
    }
    protected changeToggleState(
        state: NBuilderProductsSetup.EToggleOptions | undefined = NBuilderProductsSetup.EToggleOptions.horse
    ): void {
        if (this.storageData.optionToggleState !== state) {
            this.storageData.optionToggleState = state;
            const currentOption =
                state === NBuilderProductsSetup.EToggleOptions.rider
                    ? this.riderOptionElements?.[0]?.sectionOption
                    : this.ponyOptionElements?.[0]?.sectionOption;
            this.changeCurrentOption(currentOption);
            if (state === NBuilderProductsSetup.EToggleOptions.rider) {
                this.storageData.riderViewVisited = true;
                this.preselectRiderOptions();
            }
        }
    }

    private preselectRiderOptions(): void {
        const preselectSelection: NBuilderProductsSetup.ISelection = {};
        this.riderOptionElements
            .filter(o => o.mainOption)
            .forEach(o => {
                if (!this.storageData.selection || this.storageData.selection[o.sectionOption]) {
                    return;
                }
                const simpleProduct = o.listing?.models.find(m => m.isSimple);
                if (simpleProduct) {
                    preselectSelection[o.sectionOption] = {
                        configurableSelection: undefined,
                        productId: simpleProduct.id,
                        matchedProductId: simpleProduct.parent.id,
                        productType: NBuilderProductsSetup.EToggleOptions.rider
                    };
                }
            });
        if (this.storageData.selection && Object.keys(preselectSelection).length > 0) {
            Object.keys(preselectSelection).forEach(key => (this.storageData.selection![key] = preselectSelection[key]));
            this.updateStorage();
        }
    }

    protected override unselectCurrentOption(): void {
        if (this.currentOption?.mainOption && this.storageData.selection) {
            const type = this.storageData.selection[this.storageData.currentSectionOptionId || ''].productType;
            Object.entries(this.storageData.selection)?.forEach(([key, val]) => {
                if (val.productType === type) {
                    delete this.storageData.selection![key];
                }
            });
        }
        super.unselectCurrentOption();
    }

    private isRiderSelected(): boolean {
        return (
            this.storageData.selection &&
            Object.values(this.storageData.selection)?.find(o => o.productType === NBuilderProductsSetup.EToggleOptions.rider)
        );
    }
}
